import type { AdminPaymentsFilters, FinancePaymentStatus } from "@/api/adminPayments";
import type { StatementStatus } from "@/api/paymentStatement";

export const PAYMENT_STATUS_OPTIONS: ReadonlyArray<{ value: FinancePaymentStatus; label: string }> = [
	{ value: "submitted", label: "Submitted" },
	{ value: "in_review", label: "In review" },
	{ value: "in_process", label: "In process" },
	{ value: "on_hold", label: "On hold" },
	{ value: "rejected_by_finance", label: "Rejected" },
	{ value: "completed", label: "Paid" },
];

export const STATEMENT_STATUS_OPTIONS: ReadonlyArray<{ value: StatementStatus; label: string }> = [
	{ value: "issued", label: "Issued" },
	{ value: "confirmed", label: "Confirmed" },
	{ value: "rejected", label: "Rejected" },
	{ value: "superseded", label: "Superseded" },
];

const LIST_KEYS = ["paymentStatus", "statementStatus", "country", "currency"] as const;
const TEXT_KEYS = ["q", "issuedFrom", "issuedTo", "paidFrom", "paidTo"] as const;
const ALLOWED: Record<(typeof LIST_KEYS)[number], ReadonlySet<string> | null> = {
	paymentStatus: new Set(PAYMENT_STATUS_OPTIONS.map((o) => o.value)),
	statementStatus: new Set(STATEMENT_STATUS_OPTIONS.map((o) => o.value)),
	country: null,
	currency: null,
};

/** Filters live in the URL so a filtered view can be shared (§5.8). Unknown values are dropped. */
export function parsePaymentsSearch(params: URLSearchParams): { filters: AdminPaymentsFilters; page: number } {
	const filters: AdminPaymentsFilters = {};
	for (const key of TEXT_KEYS) {
		const value = params.get(key)?.trim();
		if (value) filters[key] = value;
	}
	for (const key of LIST_KEYS) {
		const allowed = ALLOWED[key];
		const values = (params.get(key) ?? "")
			.split(",")
			.map((v) => v.trim())
			.filter((v) => v && (!allowed || allowed.has(v)));
		if (values.length) (filters as Record<string, unknown>)[key] = values;
	}
	const page = Number.parseInt(params.get("page") ?? "1", 10);
	return { filters, page: Number.isFinite(page) && page > 0 ? page : 1 };
}

export function toPaymentsSearch(filters: AdminPaymentsFilters, page: number): URLSearchParams {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(filters)) {
		if (Array.isArray(value)) {
			if (value.length) params.set(key, value.join(","));
		} else if (typeof value === "string" && value.trim()) {
			params.set(key, value.trim());
		}
	}
	if (page > 1) params.set("page", String(page));
	return params;
}
